/**
 * Enumerations shared by the API, database constraints and UI.
 * Each list is `{ value, label }` so the UI can render selects directly.
 */
export interface Option<V extends string = string> {
  value: V;
  label: string;
}

export function values<const T extends readonly Option[]>(list: T): T[number]["value"][] {
  return list.map((o) => o.value);
}

export function labelOf(list: readonly Option[], value: string | null | undefined): string {
  return list.find((o) => o.value === value)?.label ?? value ?? "";
}

// ---------------------------------------------------------------- property

export const PROPERTY_TYPES = [
  { value: "house", label: "House" },
  { value: "flat", label: "Flat / apartment" },
  { value: "maisonette", label: "Maisonette" },
  { value: "bungalow", label: "Bungalow" },
  { value: "hmo", label: "HMO" },
  { value: "commercial", label: "Commercial" },
  { value: "mixed_use", label: "Mixed use" },
  { value: "land", label: "Land" },
  { value: "other", label: "Other" },
] as const;

/** Property types treated as non-residential for finance-cost reporting. */
export const NON_RESIDENTIAL_TYPES: readonly string[] = ["commercial", "land"];

export const PROPERTY_STATUSES = [
  { value: "active", label: "Active" },
  { value: "preparing", label: "Being prepared" },
  { value: "for_sale", label: "For sale" },
  { value: "sold", label: "Sold" },
  { value: "archived", label: "Archived" },
] as const;

// ---------------------------------------------------------------- owners

export const OWNER_TYPES = [
  { value: "individual", label: "Individual" },
  { value: "company", label: "Limited company" },
] as const;

export const ACCOUNTING_BASES = [
  { value: "cash", label: "Cash basis" },
  { value: "accruals", label: "Accruals (traditional) basis" },
] as const;

export const MTD_STATUSES = [
  { value: "not_signed_up", label: "Not signed up" },
  { value: "voluntary", label: "Signed up voluntarily" },
  { value: "mandated", label: "Mandated" },
  { value: "exempt", label: "Exempt" },
] as const;

export const QUARTERLY_PERIOD_TYPES = [
  { value: "standard", label: "Standard (6th to 5th)" },
  { value: "calendar", label: "Calendar (1st to month end)" },
] as const;

// ---------------------------------------------------------------- tenancy

export const TENANCY_TYPES = [
  { value: "ast", label: "Assured shorthold tenancy" },
  { value: "periodic", label: "Periodic tenancy" },
  { value: "company_let", label: "Company let" },
  { value: "licence", label: "Licence / lodger" },
  { value: "other", label: "Other" },
] as const;

export const TENANCY_STATUSES = [
  { value: "upcoming", label: "Upcoming" },
  { value: "active", label: "Active" },
  { value: "periodic", label: "Rolling / periodic" },
  { value: "ended", label: "Ended" },
] as const;

export const DEPOSIT_SCHEMES = [
  { value: "dps", label: "Deposit Protection Service" },
  { value: "mydeposits", label: "mydeposits" },
  { value: "tds", label: "Tenancy Deposit Scheme" },
  { value: "safedeposits_scotland", label: "SafeDeposits Scotland" },
  { value: "other", label: "Other" },
  { value: "none", label: "Not protected / no deposit" },
] as const;

export const RENT_FREQUENCIES = [
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "four_weekly", label: "Every 4 weeks" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
] as const;

export type RentFrequency = (typeof RENT_FREQUENCIES)[number]["value"];

export const RENT_CHANGE_REASONS = [
  { value: "initial", label: "Initial rent" },
  { value: "review", label: "Rent review" },
  { value: "renewal", label: "Renewal" },
  { value: "negotiated", label: "Negotiated" },
  { value: "correction", label: "Correction" },
] as const;

// ---------------------------------------------------------------- HMRC / tax

/**
 * HMRC UK property income categories (Property Business API, periodic summary).
 * Field names mirror the API; verify against the current specification.
 */
export const HMRC_INCOME_CATEGORIES = [
  { value: "periodAmount", label: "Rent and other receipts (periodAmount)" },
  { value: "premiumsOfLeaseGrant", label: "Premiums for the grant of a lease" },
  { value: "reversePremiums", label: "Reverse premiums" },
  { value: "otherIncome", label: "Other property income" },
] as const;

export const HMRC_EXPENSE_CATEGORIES = [
  { value: "premisesRunningCosts", label: "Rent, rates, insurance and ground rents" },
  { value: "repairsAndMaintenance", label: "Property repairs and maintenance" },
  { value: "financialCosts", label: "Loan interest and other financial costs (non-residential)" },
  { value: "professionalFees", label: "Legal, management and other professional fees" },
  { value: "costOfServices", label: "Costs of services provided, including wages" },
  { value: "travelCosts", label: "Travel costs" },
  { value: "other", label: "Other allowable property expenses" },
  { value: "residentialFinancialCost", label: "Residential property finance costs" },
] as const;

export type HmrcExpenseCategory = (typeof HMRC_EXPENSE_CATEGORIES)[number]["value"];
export type HmrcIncomeCategory = (typeof HMRC_INCOME_CATEGORIES)[number]["value"];

export const INCOME_TYPES = [
  { value: "rent", label: "Rent", hmrc: "periodAmount" },
  { value: "deposit_retained", label: "Deposit retained (e.g. damages)", hmrc: "otherIncome" },
  { value: "service_charge", label: "Service charges / bills recharged", hmrc: "otherIncome" },
  { value: "insurance_payout", label: "Insurance payout", hmrc: "otherIncome" },
  { value: "lease_premium", label: "Lease premium", hmrc: "premiumsOfLeaseGrant" },
  { value: "reverse_premium", label: "Reverse premium", hmrc: "reversePremiums" },
  { value: "other_property", label: "Other property income", hmrc: "otherIncome" },
  { value: "not_reportable", label: "Other (not property income)", hmrc: null },
] as const satisfies ReadonlyArray<Option & { hmrc: HmrcIncomeCategory | null }>;

export type IncomeType = (typeof INCOME_TYPES)[number]["value"];

export function hmrcIncomeCategory(type: string): HmrcIncomeCategory | null {
  return INCOME_TYPES.find((t) => t.value === type)?.hmrc ?? null;
}

/**
 * How an expense is treated in reports. This is a record-keeping label chosen by the user;
 * it is not a statement that the cost is deductible.
 */
export const EXPENSE_TREATMENTS = [
  { value: "revenue", label: "Revenue (operating) expense" },
  { value: "finance_cost", label: "Finance cost (interest / loan fees)" },
  { value: "capital", label: "Capital expenditure" },
  { value: "replacement_domestic_items", label: "Replacement of domestic items" },
  { value: "non_allowable", label: "Private / non-allowable" },
] as const;

export type ExpenseTreatment = (typeof EXPENSE_TREATMENTS)[number]["value"];

/** Default expense categories seeded for new teams (teams may edit them). */
export const DEFAULT_EXPENSE_CATEGORIES: ReadonlyArray<{
  name: string;
  treatment: ExpenseTreatment;
  hmrc: HmrcExpenseCategory | null;
}> = [
  { name: "Letting & management fees", treatment: "revenue", hmrc: "professionalFees" },
  { name: "Legal & accountancy", treatment: "revenue", hmrc: "professionalFees" },
  { name: "Insurance", treatment: "revenue", hmrc: "premisesRunningCosts" },
  { name: "Ground rent & service charges", treatment: "revenue", hmrc: "premisesRunningCosts" },
  { name: "Council tax & utilities", treatment: "revenue", hmrc: "premisesRunningCosts" },
  { name: "Repairs & maintenance", treatment: "revenue", hmrc: "repairsAndMaintenance" },
  { name: "Cleaning & gardening", treatment: "revenue", hmrc: "costOfServices" },
  { name: "Safety certificates & compliance", treatment: "revenue", hmrc: "other" },
  { name: "Advertising & tenant finding", treatment: "revenue", hmrc: "other" },
  { name: "Office, phone & stationery", treatment: "revenue", hmrc: "other" },
  { name: "Travel", treatment: "revenue", hmrc: "travelCosts" },
  { name: "Mortgage interest", treatment: "finance_cost", hmrc: "residentialFinancialCost" },
  { name: "Loan fees", treatment: "finance_cost", hmrc: "residentialFinancialCost" },
  { name: "Capital improvements", treatment: "capital", hmrc: null },
  { name: "Purchase / sale costs", treatment: "capital", hmrc: null },
  { name: "Replacement furnishings & appliances", treatment: "replacement_domestic_items", hmrc: null },
  { name: "Private / non-allowable", treatment: "non_allowable", hmrc: null },
];

export const PAYMENT_METHODS = [
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "card", label: "Card" },
  { value: "direct_debit", label: "Direct debit" },
  { value: "standing_order", label: "Standing order" },
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "agent_deducted", label: "Deducted by agent" },
  { value: "other", label: "Other" },
] as const;

// ---------------------------------------------------------------- finance

export const LOAN_TYPES = [
  { value: "repayment", label: "Repayment mortgage" },
  { value: "interest_only", label: "Interest-only mortgage" },
  { value: "bridging", label: "Bridging loan" },
  { value: "secured_loan", label: "Other secured loan" },
  { value: "unsecured_loan", label: "Unsecured loan" },
] as const;

export const RATE_TYPES = [
  { value: "fixed", label: "Fixed" },
  { value: "tracker", label: "Tracker" },
  { value: "variable", label: "Variable / SVR" },
  { value: "discount", label: "Discount" },
] as const;

export const LOAN_TRANSACTION_TYPES = [
  { value: "payment", label: "Payment (interest + capital)" },
  { value: "fee", label: "Fee" },
  { value: "drawdown", label: "Drawdown / further advance" },
  { value: "redemption", label: "Redemption" },
] as const;

// ---------------------------------------------------------------- ops

export const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
] as const;

export const TASK_STATUSES = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "waiting", label: "Waiting" },
  { value: "done", label: "Done" },
  { value: "cancelled", label: "Cancelled" },
] as const;

export const CLOSED_TASK_STATUSES: readonly string[] = ["done", "cancelled"];

export const RECURRENCE_FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
] as const;

export const MAINTENANCE_STATUSES = [
  { value: "reported", label: "Reported" },
  { value: "investigating", label: "Investigating" },
  { value: "scheduled", label: "Scheduled" },
  { value: "in_progress", label: "In progress" },
  { value: "awaiting_parts", label: "Awaiting parts" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
] as const;

export const CLOSED_MAINTENANCE_STATUSES: readonly string[] = ["completed", "cancelled"];

export const COMMUNICATION_METHODS = [
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "sms", label: "SMS" },
  { value: "letter", label: "Letter" },
  { value: "in_person", label: "In person" },
  { value: "messaging", label: "Messaging app" },
  { value: "other", label: "Other" },
] as const;

export const COMMUNICATION_DIRECTIONS = [
  { value: "inbound", label: "Inbound (from tenant)" },
  { value: "outbound", label: "Outbound (to tenant)" },
] as const;

export const COMPLIANCE_STATUSES = [
  { value: "current", label: "Current" },
  { value: "expiring", label: "Expiring soon" },
  { value: "expired", label: "Expired" },
  { value: "superseded", label: "Superseded" },
  { value: "not_applicable", label: "Not applicable" },
] as const;

/** Default compliance requirement types seeded for new teams. */
export const DEFAULT_COMPLIANCE_TYPES: ReadonlyArray<{
  name: string;
  validityMonths: number | null;
  reminderDays: number[];
}> = [
  { name: "Gas safety certificate", validityMonths: 12, reminderDays: [60, 30, 7] },
  { name: "Electrical installation condition report (EICR)", validityMonths: 60, reminderDays: [90, 30] },
  { name: "Energy performance certificate (EPC)", validityMonths: 120, reminderDays: [90, 30] },
  { name: "Landlord insurance", validityMonths: 12, reminderDays: [45, 14] },
  { name: "Smoke & CO alarm check", validityMonths: 12, reminderDays: [30, 7] },
  { name: "Legionella risk assessment", validityMonths: 24, reminderDays: [60, 14] },
  { name: "PAT testing", validityMonths: 12, reminderDays: [30] },
  { name: "Fire risk assessment", validityMonths: 12, reminderDays: [60, 14] },
  { name: "HMO licence", validityMonths: 60, reminderDays: [180, 90, 30] },
  { name: "Selective licence", validityMonths: 60, reminderDays: [180, 90, 30] },
];

// ---------------------------------------------------------------- documents

export const DOCUMENT_CATEGORIES = [
  { value: "receipt", label: "Receipt" },
  { value: "invoice", label: "Invoice" },
  { value: "bill", label: "Bill" },
  { value: "statement", label: "Statement" },
  { value: "certificate", label: "Certificate" },
  { value: "tenancy_agreement", label: "Tenancy agreement" },
  { value: "inventory", label: "Inventory" },
  { value: "photo", label: "Photo" },
  { value: "correspondence", label: "Correspondence" },
  { value: "legal", label: "Legal" },
  { value: "insurance", label: "Insurance" },
  { value: "mortgage", label: "Mortgage / finance" },
  { value: "other", label: "Other" },
] as const;

/** Allowed upload MIME types → accepted file extensions. */
export const ALLOWED_UPLOAD_TYPES: Record<string, string[]> = {
  "application/pdf": ["pdf"],
  "image/jpeg": ["jpg", "jpeg"],
  "image/png": ["png"],
  "image/webp": ["webp"],
  "image/heic": ["heic"],
  "text/plain": ["txt"],
  "text/csv": ["csv"],
  "application/msword": ["doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ["docx"],
  "application/vnd.ms-excel": ["xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ["xlsx"],
  "application/vnd.oasis.opendocument.text": ["odt"],
  "application/vnd.oasis.opendocument.spreadsheet": ["ods"],
};

/** Record types a document can be attached to. */
export const ATTACHABLE_TYPES = [
  "property",
  "tenancy",
  "tenant",
  "income",
  "expense",
  "task",
  "maintenance",
  "communication",
  "compliance",
  "contractor",
  "loan",
  "tax_owner",
] as const;

export type AttachableType = (typeof ATTACHABLE_TYPES)[number];

export const NOT_TAX_ADVICE =
  "This application records and reports your information. It does not provide tax advice. " +
  "Assigning a category to a transaction does not mean it is allowable or deductible for tax purposes — " +
  "please confirm treatment with a qualified accountant or tax adviser.";
